<script setup lang="ts">
import { PromotionType } from '@rediredi/types'
import { useFlag } from '@unleash/proxy-client-vue'
import { FALLBACK_IMAGE_PATH } from '~/consts/images'
import { IStorefrontItem } from '~/types/item'

const props = defineProps<{
  item: IStorefrontItem
}>()

const emit = defineEmits<{
  (e: 'view-product', item: IStorefrontItem): void
  (e: 'add-product', item: IStorefrontItem): void
}>()

const { event } = useEvent()
const globalStore = useGlobalStore()
const discountStore = useDiscountStore()
const isVariationPriceEnabled = useFlag('storefront.variation-price')

const handleClick = () => {
  emit('view-product', props.item)
  event('select_item', {
    item_list_id: 'main_search',
    item_list_name: 'Main search',
    items: [itemToAnalytics(props.item)],
  })
}

const imgSrc = computed(() => {
  const pictures = getCoverVariantPictures(props.item)
  if (pictures.length) return optimizedImage(getPictureUrl(pictures[0]), 'small')
  return FALLBACK_IMAGE_PATH
})

const isOutOfStock = computed(() => (props.item.quantityAvailable || 0) < 1)

const currency = globalStore.storeCurrency

const price = computed(() => {
  if (isVariationPriceEnabled.value) {
    const cover = getCoverVariant(props.item)

    if (props.item.promotion && props.item.promotion.type !== PromotionType.Label) {
      return discountStore.getDiscountedPrice(cover.promotionPrice!)
    } else {
      return discountStore.getDiscountedPrice(cover.price!)
    }
  }

  return props.item.promotion && props.item.promotion.type !== PromotionType.Label
    ? discountStore.getDiscountedPrice(props.item.baseVariant.promotionPrice!)
    : discountStore.getDiscountedPrice(props.item.baseVariant.price!)
})
</script>

<template>
  <div
    class="relative flex h-full cursor-pointer flex-col justify-between overflow-hidden border-1 border-white bg-white transition-all duration-300"
  >
    <RMosaicProductIndicator
      v-if="isOutOfStock || props.item.promotion"
      :type="isOutOfStock ? 'outOfStock' : 'promotion'"
      :promotion="props.item.promotion"
      context="grid"
    />

    <div role="button" class="h-full" @click="handleClick">
      <img
        alt=""
        loading="lazy"
        class="flex aspect-square w-full flex-shrink-0 flex-grow-0 justify-center object-contain align-middle"
        :class="{
          'p-8 sm:p-16': imgSrc === FALLBACK_IMAGE_PATH,
        }"
        :src="imgSrc"
      />
    </div>
    <div
      v-if="price?.amount"
      class="absolute bottom-1 left-1 flex flex-col justify-end justify-items-end rounded-sm bg-white bg-opacity-80 px-1 py-0.5 text-gray-400 invert-[7%] backdrop-blur-sm"
    >
      <div class="self-end text-[12px] font-extrabold">
        <span>{{ formatPriceInParts(price.amount, { currency }).currency }}</span>
        <span>{{ formatPriceInParts(price.amount, { currency }).amount }}</span>
      </div>
    </div>
  </div>
</template>
